import clsx from 'clsx';
import { ComponentPropsWithoutRef, FC, useRef } from 'react';
import { Icon, IconName } from '../Icon';
import { ButtonTheme } from './types';
import { elementsButtonClasses } from './util';
import { ClientOnly } from 'remix-utils/client-only';
import { useHoverDirty } from 'react-use';
import { motion, MotionProps } from 'framer-motion';
export interface IconButtonProps
  extends Pick<ComponentPropsWithoutRef<'button'>, 'onClick' | 'type' | 'className' | 'disabled' | 'style' | 'aria-label'> {
  isLoading?: boolean;
  icon: JSX.Element | IconName;
  iconClassName?: string;
  diameter?: number;
  theme?: ButtonTheme;
  hoverTheme?: ButtonTheme;
  motionProps?: MotionProps;
}

const IconButtonInner: FC<IconButtonProps> = ({
  type = 'button',
  diameter = 40,
  icon,
  iconClassName,
  className,
  theme,
  isLoading,
  hoverTheme,
  disabled,
  style,
  onClick,
  motionProps,
  'aria-label': ariaLabel,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hovered = useHoverDirty(buttonRef);

  const runtimeTheme = hovered && hoverTheme ? hoverTheme : theme;

  return (
    <motion.button
      disabled={disabled}
      ref={buttonRef}
      style={{
        height: diameter,
        width: diameter,
        ...style,
      }}
      type={type}
      aria-label={ariaLabel}
      onClick={(e) => {
        if (isLoading || disabled) return e.preventDefault();
        onClick?.(e);
      }}
      className={clsx(
        'p-0',
        className,
        elementsButtonClasses.base({ disabled }),
        elementsButtonClasses.theme(runtimeTheme)
      )}
      {...motionProps}
    >
      <div className={clsx('contents IconButton-icon', elementsButtonClasses.iconTheme(runtimeTheme))}>
        {isLoading ? (
          <Icon icon="spinner" />
        ) : typeof icon === 'string' ? (
          <Icon icon={icon} className={iconClassName} />
        ) : (
          icon
        )}
      </div>
    </motion.button>
  );
};

export const IconButton: FC<IconButtonProps> = (props) => (
  <ClientOnly>{() => <IconButtonInner {...props} />}</ClientOnly>
);
